import { FC, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { BatteryType } from '@/hooks/useBatteryContainers';

interface AddToOrderBatteryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batteryTypes: BatteryType[];
  onSubmit: (data: {
    battery_type_id: string;
    serial_number?: string;
    notes?: string;
  }) => void;
}

export const AddToOrderBatteryModal: FC<AddToOrderBatteryModalProps> = ({
  open,
  onOpenChange,
  batteryTypes,
  onSubmit,
}) => {
  const [batteryTypeId, setBatteryTypeId] = useState<string>('');
  const [serialNumber, setSerialNumber] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = () => {
    if (!batteryTypeId) return;

    onSubmit({
      battery_type_id: batteryTypeId,
      serial_number: serialNumber || undefined,
      notes: notes || undefined,
    });

    // Reset form
    setBatteryTypeId('');
    setSerialNumber('');
    setNotes('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Batterij toevoegen aan Te Bestellen</DialogTitle>
          <DialogDescription>
            Voeg een nieuwe batterij toe aan de bestellijst
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="battery-type">Batterij Type *</Label>
            <Select value={batteryTypeId} onValueChange={setBatteryTypeId}>
              <SelectTrigger id="battery-type">
                <SelectValue placeholder="Selecteer type" />
              </SelectTrigger>
              <SelectContent>
                {batteryTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name} ({type.power_kw}kW / {type.capacity_kwh}kWh)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="serial-number">Serienummer (optioneel)</Label>
            <Input
              id="serial-number"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="Bijv. BAT-2024-001"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notities (optioneel)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Bijv. besteld bij leverancier X"
              rows={3}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Annuleren
            </Button>
            <Button onClick={handleSubmit} disabled={!batteryTypeId} className="flex-1">
              Toevoegen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
